import globalAgent from 'src/agent';

export type CrmProvider = 'amocrm' | 'bitrix24';

export type CrmConnectionHealth = 'draft' | 'ready' | 'error';

export type CrmConnectionStats = {
    total: number;
    synced: number;
    errors: number;
    inWork: number;
    won: number;
    lost: number;
};

export type CrmPipelineStage = {
    id: string;
    name: string;
    sort: number;
    color?: string;
    semantics?: string;
    isEditable?: boolean;
};

export type CrmPipeline = {
    id: string;
    name: string;
    isMain?: boolean;
    stages: CrmPipelineStage[];
};

export type CrmConnection = {
    id: number;
    provider: CrmProvider;
    providerLabel: string;
    name: string;
    isActive: boolean;
    syncNewLeads: boolean;
    syncDialogueMessages: boolean;
    syncPipelineStages: boolean;
    baseUrl: string;
    webhookUrl: string;
    clientId: string;
    redirectUri: string;
    selectedPipelineId: string;
    selectedPipelineName: string;
    pipelineSnapshot: CrmPipeline[];
    stageMapping: Record<string, string>;
    healthStatus: CrmConnectionHealth;
    lastCheckedAt: string;
    lastSyncAt: string;
    lastError: string;
    remoteAccountId: string;
    responsibleUserId: string;
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    hasClientSecret: boolean;
    stats: CrmConnectionStats;
    createdAt: string;
    updatedAt: string;
};

export type CrmConnectionPayload = {
    provider: CrmProvider;
    name?: string;
    isActive?: boolean;
    syncNewLeads?: boolean;
    syncDialogueMessages?: boolean;
    syncPipelineStages?: boolean;
    baseUrl?: string;
    webhookUrl?: string;
    clientId?: string;
    clientSecret?: string;
    redirectUri?: string;
    authorizationCode?: string;
    accessToken?: string;
    refreshToken?: string;
    selectedPipelineId?: string;
    selectedPipelineName?: string;
    stageMapping?: Record<string, string>;
    responsibleUserId?: string;
};

export type CrmLeadBinding = {
    id: number;
    leadId: number;
    leadCategory: string;
    leadAuthor: string;
    leadMessage: string;
    conversationStatus: string;
    conversationStage: string;
    remoteContactId: string;
    remoteDealId: string;
    remotePipelineId: string;
    remoteStageId: string;
    remoteStageName: string;
    syncStatus: string;
    lastSyncedAt: string;
    lastError: string;
};

export type CrmConnectionTestResult = {
    connection: CrmConnection;
    pipelines: CrmPipeline[];
    stageMapping: Record<string, string>;
};

export type CrmSyncNowResult = {
    summary: {
        messageStreams: number;
        conversations: number;
        bindings: number;
    };
    connection: CrmConnection;
};

type BackendCrmPipelineStage = {
    id?: string | number;
    name?: string;
    sort?: string | number;
    color?: string;
    semantics?: string;
    is_editable?: boolean;
};

type BackendCrmPipeline = {
    id?: string | number;
    name?: string;
    is_main?: boolean;
    stages?: BackendCrmPipelineStage[];
};

type BackendCrmConnection = {
    id?: number;
    provider?: string;
    provider_label?: string;
    name?: string;
    is_active?: boolean;
    sync_new_leads?: boolean;
    sync_dialogue_messages?: boolean;
    sync_pipeline_stages?: boolean;
    base_url?: string;
    webhook_url?: string;
    client_id?: string;
    redirect_uri?: string;
    selected_pipeline_id?: string;
    selected_pipeline_name?: string;
    pipeline_snapshot?: BackendCrmPipeline[];
    stage_mapping?: Record<string, string>;
    health_status?: string;
    last_checked_at?: string | null;
    last_sync_at?: string | null;
    last_error?: string;
    remote_account_id?: string;
    responsible_user_id?: string;
    has_access_token?: boolean;
    has_refresh_token?: boolean;
    has_client_secret?: boolean;
    stats?: {
        total?: number;
        synced?: number;
        errors?: number;
        in_work?: number;
        won?: number;
        lost?: number;
    };
    created_at?: string;
    updated_at?: string;
};

type BackendCrmLeadBinding = {
    id?: number;
    lead_id?: number;
    lead_category?: string;
    lead_author?: string;
    lead_message?: string;
    conversation_status?: string;
    conversation_stage?: string;
    remote_contact_id?: string;
    remote_deal_id?: string;
    remote_pipeline_id?: string;
    remote_stage_id?: string;
    remote_stage_name?: string;
    sync_status?: string;
    last_synced_at?: string | null;
    last_error?: string;
};

type BackendCrmSyncNowResult = {
    summary?: {
        message_streams?: number;
        conversations?: number;
        bindings?: number;
    };
    connection?: BackendCrmConnection;
};

function unwrapList<T>(value: T[] | { results?: T[] }): T[] {
    return Array.isArray(value) ? value : value.results || [];
}

function normalizeProvider(value?: string): CrmProvider {
    return value === 'bitrix24' ? 'bitrix24' : 'amocrm';
}

function normalizeHealth(value?: string): CrmConnectionHealth {
    if (value === 'ready' || value === 'error') {
        return value;
    }
    return 'draft';
}

function normalizePipelineStage(stage: BackendCrmPipelineStage): CrmPipelineStage {
    return {
        id: String(stage.id || ''),
        name: String(stage.name || ''),
        sort: Number(stage.sort || 0),
        color: stage.color || '',
        semantics: stage.semantics || '',
        isEditable: Boolean(stage.is_editable),
    };
}

function normalizePipeline(pipeline: BackendCrmPipeline): CrmPipeline {
    return {
        id: String(pipeline.id || ''),
        name: String(pipeline.name || ''),
        isMain: Boolean(pipeline.is_main),
        stages: (pipeline.stages || []).map(normalizePipelineStage),
    };
}

function normalizeStats(stats?: BackendCrmConnection['stats']): CrmConnectionStats {
    return {
        total: Number(stats?.total || 0),
        synced: Number(stats?.synced || 0),
        errors: Number(stats?.errors || 0),
        inWork: Number(stats?.in_work || 0),
        won: Number(stats?.won || 0),
        lost: Number(stats?.lost || 0),
    };
}

function normalizeConnection(connection: BackendCrmConnection): CrmConnection {
    const provider = normalizeProvider(connection.provider);

    return {
        id: Number(connection.id || 0),
        provider,
        providerLabel: connection.provider_label || (provider === 'amocrm' ? 'AmoCRM' : 'Bitrix24'),
        name: connection.name || '',
        isActive: connection.is_active !== false,
        syncNewLeads: connection.sync_new_leads !== false,
        syncDialogueMessages: connection.sync_dialogue_messages !== false,
        syncPipelineStages: connection.sync_pipeline_stages !== false,
        baseUrl: connection.base_url || '',
        webhookUrl: connection.webhook_url || '',
        clientId: connection.client_id || '',
        redirectUri: connection.redirect_uri || '',
        selectedPipelineId: connection.selected_pipeline_id || '',
        selectedPipelineName: connection.selected_pipeline_name || '',
        pipelineSnapshot: (connection.pipeline_snapshot || []).map(normalizePipeline),
        stageMapping: connection.stage_mapping || {},
        healthStatus: normalizeHealth(connection.health_status),
        lastCheckedAt: connection.last_checked_at || '',
        lastSyncAt: connection.last_sync_at || '',
        lastError: connection.last_error || '',
        remoteAccountId: connection.remote_account_id || '',
        responsibleUserId: connection.responsible_user_id || '',
        hasAccessToken: Boolean(connection.has_access_token),
        hasRefreshToken: Boolean(connection.has_refresh_token),
        hasClientSecret: Boolean(connection.has_client_secret),
        stats: normalizeStats(connection.stats),
        createdAt: connection.created_at || '',
        updatedAt: connection.updated_at || '',
    };
}

function normalizeBinding(binding: BackendCrmLeadBinding): CrmLeadBinding {
    return {
        id: Number(binding.id || 0),
        leadId: Number(binding.lead_id || 0),
        leadCategory: binding.lead_category || '',
        leadAuthor: binding.lead_author || '',
        leadMessage: binding.lead_message || '',
        conversationStatus: binding.conversation_status || '',
        conversationStage: binding.conversation_stage || '',
        remoteContactId: binding.remote_contact_id || '',
        remoteDealId: binding.remote_deal_id || '',
        remotePipelineId: binding.remote_pipeline_id || '',
        remoteStageId: binding.remote_stage_id || '',
        remoteStageName: binding.remote_stage_name || '',
        syncStatus: binding.sync_status || '',
        lastSyncedAt: binding.last_synced_at || '',
        lastError: binding.last_error || '',
    };
}

function toBackendPayload(payload: Partial<CrmConnectionPayload>): Record<string, unknown> {
    return {
        ...(payload.provider !== undefined ? { provider: payload.provider } : {}),
        ...(payload.name !== undefined ? { name: payload.name } : {}),
        ...(payload.isActive !== undefined ? { is_active: payload.isActive } : {}),
        ...(payload.syncNewLeads !== undefined ? { sync_new_leads: payload.syncNewLeads } : {}),
        ...(payload.syncDialogueMessages !== undefined ? { sync_dialogue_messages: payload.syncDialogueMessages } : {}),
        ...(payload.syncPipelineStages !== undefined ? { sync_pipeline_stages: payload.syncPipelineStages } : {}),
        ...(payload.baseUrl !== undefined ? { base_url: payload.baseUrl } : {}),
        ...(payload.webhookUrl !== undefined ? { webhook_url: payload.webhookUrl } : {}),
        ...(payload.clientId !== undefined ? { client_id: payload.clientId } : {}),
        ...(payload.clientSecret !== undefined ? { client_secret: payload.clientSecret } : {}),
        ...(payload.redirectUri !== undefined ? { redirect_uri: payload.redirectUri } : {}),
        ...(payload.authorizationCode !== undefined ? { authorization_code: payload.authorizationCode } : {}),
        ...(payload.accessToken !== undefined ? { access_token: payload.accessToken } : {}),
        ...(payload.refreshToken !== undefined ? { refresh_token: payload.refreshToken } : {}),
        ...(payload.selectedPipelineId !== undefined ? { selected_pipeline_id: payload.selectedPipelineId } : {}),
        ...(payload.selectedPipelineName !== undefined ? { selected_pipeline_name: payload.selectedPipelineName } : {}),
        ...(payload.stageMapping !== undefined ? { stage_mapping: payload.stageMapping } : {}),
        ...(payload.responsibleUserId !== undefined ? { responsible_user_id: payload.responsibleUserId } : {}),
    };
}

class CrmIntegrationsService {
    listConnections = async (): Promise<CrmConnection[]> => {
        const { data } = await globalAgent.get<BackendCrmConnection[] | { results?: BackendCrmConnection[] }>(
            '/crm/connections/'
        );
        return unwrapList(data).map(normalizeConnection);
    };

    saveConnection = async (payload: CrmConnectionPayload): Promise<CrmConnection> => {
        const { data } = await globalAgent.post<BackendCrmConnection>('/crm/connections/', toBackendPayload(payload));
        return normalizeConnection(data);
    };

    updateConnection = async (id: number, payload: Partial<CrmConnectionPayload>): Promise<CrmConnection> => {
        const { data } = await globalAgent.patch<BackendCrmConnection>(
            `/crm/connections/${id}/`,
            toBackendPayload(payload)
        );
        return normalizeConnection(data);
    };

    testConnection = async (id: number): Promise<CrmConnectionTestResult> => {
        const { data } = await globalAgent.post<{
            connection?: BackendCrmConnection;
            pipelines?: BackendCrmPipeline[];
            stage_mapping?: Record<string, string>;
        }>(`/crm/connections/${id}/test/`);
        return {
            connection: normalizeConnection(data.connection || {}),
            pipelines: (data.pipelines || []).map(normalizePipeline),
            stageMapping: data.stage_mapping || {},
        };
    };

    syncNow = async (id: number): Promise<CrmSyncNowResult> => {
        const { data } = await globalAgent.post<BackendCrmSyncNowResult>(`/crm/connections/${id}/sync-now/`);
        return {
            summary: {
                messageStreams: Number(data.summary?.message_streams || 0),
                conversations: Number(data.summary?.conversations || 0),
                bindings: Number(data.summary?.bindings || 0),
            },
            connection: normalizeConnection(data.connection || {}),
        };
    };

    listBindings = async (connectionId: number): Promise<CrmLeadBinding[]> => {
        const { data } = await globalAgent.get<BackendCrmLeadBinding[]>(`/crm/connections/${connectionId}/bindings/`);
        return data.map(normalizeBinding);
    };
}

export default new CrmIntegrationsService();
